import type { SampleRegion } from "@/core/field/brushes";
import { applyDab, unionRegion, type BrushDab } from "@/core/field/brushes";
import { regularizeRegion } from "@/core/field/regularize";
import type { VoxelField } from "@/core/field/VoxelField";
import type { HistoryCommand } from "./HistoryStack";

const BLOCK = 16;

/**
 * Records the field as it was before a stroke, one 16^3 sample block at a
 * time, and turns the finished stroke into an undo command.
 *
 * Undo restores the recorded blocks. Redo replays the stroke's dabs from that
 * restored state — brush application and topology regularisation are both
 * deterministic, so the replay lands on exactly the state undo removed, at half
 * the memory of storing before and after.
 */
export class StrokeRecorder {
  private readonly before = new Map<number, { data: Float32Array; dirty: Uint8Array }>();
  private readonly dabs: BrushDab[] = [];
  private region: SampleRegion | null = null;

  private readonly bx: number;
  private readonly by: number;
  private readonly bz: number;

  constructor(private readonly field: VoxelField) {
    this.bx = Math.ceil(field.nx / BLOCK);
    this.by = Math.ceil(field.ny / BLOCK);
    this.bz = Math.ceil(field.nz / BLOCK);
  }

  get touched(): SampleRegion | null {
    return this.region;
  }

  get isEmpty(): boolean {
    return this.dabs.length === 0 || this.region === null;
  }

  /** Apply one dab, recording whatever it is about to overwrite. */
  apply(dab: BrushDab): SampleRegion | null {
    const probe = probeRegion(this.field, dab);
    this.captureBlocks(probe);
    const touched = applyDab(this.field, dab);
    if (!touched) return null;
    this.dabs.push(dab);
    this.region = unionRegion(this.region, touched);
    return touched;
  }

  /** Clean up ambiguous voxel configurations left by this batch of dabs. */
  regularize(region: SampleRegion): void {
    const padded = padRegion(this.field, region, 2);
    this.captureBlocks(padded);
    regularizeRegion(this.field, region);
  }

  private captureBlocks(region: SampleRegion): void {
    const { field } = this;
    const b0 = Math.floor(region.i0 / BLOCK);
    const b1 = Math.floor((region.i1 - 1) / BLOCK);
    const c0 = Math.floor(region.j0 / BLOCK);
    const c1 = Math.floor((region.j1 - 1) / BLOCK);
    const d0 = Math.floor(region.k0 / BLOCK);
    const d1 = Math.floor((region.k1 - 1) / BLOCK);

    for (let d = Math.max(0, d0); d <= Math.min(this.bz - 1, d1); d++) {
      for (let c = Math.max(0, c0); c <= Math.min(this.by - 1, c1); c++) {
        for (let b = Math.max(0, b0); b <= Math.min(this.bx - 1, b1); b++) {
          const id = b + this.bx * (c + this.by * d);
          if (this.before.has(id)) continue;

          const iw = Math.min(BLOCK, field.nx - b * BLOCK);
          const jh = Math.min(BLOCK, field.ny - c * BLOCK);
          const kd = Math.min(BLOCK, field.nz - d * BLOCK);
          const data = new Float32Array(iw * jh * kd);
          const dirty = new Uint8Array(iw * jh * kd);
          let at = 0;
          for (let k = 0; k < kd; k++) {
            for (let j = 0; j < jh; j++) {
              const from = field.index(b * BLOCK, c * BLOCK + j, d * BLOCK + k);
              data.set(field.data.subarray(from, from + iw), at);
              dirty.set(field.dirty.subarray(from, from + iw), at);
              at += iw;
            }
          }
          this.before.set(id, { data, dirty });
        }
      }
    }
  }

  private restore(): void {
    const { field } = this;
    for (const [id, block] of this.before) {
      const b = id % this.bx;
      const c = Math.floor(id / this.bx) % this.by;
      const d = Math.floor(id / (this.bx * this.by));
      const iw = Math.min(BLOCK, field.nx - b * BLOCK);
      const jh = Math.min(BLOCK, field.ny - c * BLOCK);
      const kd = Math.min(BLOCK, field.nz - d * BLOCK);
      let at = 0;
      for (let k = 0; k < kd; k++) {
        for (let j = 0; j < jh; j++) {
          const to = field.index(b * BLOCK, c * BLOCK + j, d * BLOCK + k);
          field.data.set(block.data.subarray(at, at + iw), to);
          field.dirty.set(block.dirty.subarray(at, at + iw), to);
          at += iw;
        }
      }
    }
  }

  private replay(): void {
    for (const dab of this.dabs) applyDab(this.field, dab);
    if (this.region) regularizeRegion(this.field, this.region);
  }

  /** Freeze the stroke into an undoable command. Returns null for a no-op. */
  toCommand(label: string, onChange: (region: SampleRegion) => void): HistoryCommand | null {
    if (this.isEmpty || !this.region) return null;
    const region = this.region;
    let bytes = 0;
    for (const block of this.before.values()) {
      bytes += block.data.byteLength + block.dirty.byteLength;
    }
    return {
      label,
      bytes,
      undo: () => {
        this.restore();
        onChange(region);
      },
      redo: () => {
        this.restore();
        this.replay();
        onChange(region);
      },
    };
  }
}

/** The samples a dab could touch, without applying it. */
function probeRegion(field: VoxelField, dab: BrushDab): SampleRegion {
  const r = dab.radius + field.h * 2;
  const lo = (v: number, o: number, n: number): number =>
    Math.max(0, Math.min(n - 1, Math.floor((v - r - o) / field.h)));
  const hi = (v: number, o: number, n: number): number =>
    Math.max(0, Math.min(n - 1, Math.ceil((v + r - o) / field.h)));
  return {
    i0: lo(dab.x, field.ox, field.nx),
    i1: hi(dab.x, field.ox, field.nx) + 1,
    j0: lo(dab.y, field.oy, field.ny),
    j1: hi(dab.y, field.oy, field.ny) + 1,
    k0: lo(dab.z, field.oz, field.nz),
    k1: hi(dab.z, field.oz, field.nz) + 1,
  };
}

function padRegion(field: VoxelField, region: SampleRegion, pad: number): SampleRegion {
  return {
    i0: Math.max(0, region.i0 - pad),
    i1: Math.min(field.nx, region.i1 + pad),
    j0: Math.max(0, region.j0 - pad),
    j1: Math.min(field.ny, region.j1 + pad),
    k0: Math.max(0, region.k0 - pad),
    k1: Math.min(field.nz, region.k1 + pad),
  };
}
